import { StatsCard } from '@/components/admin/stats-card';
import { type DashboardStats, admin } from '@/lib/admin-api';
import { routes } from '@/lib/routes';
import {
  AlertTriangle,
  FileText,
  Flag,
  Loader2,
  Monitor,
  ShieldAlert,
  UserCheck,
  UserMinus,
  UserX,
  Users
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

export default function AdminDashboardPage() {
  const { t } = useTranslation();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await admin.getStats();
        setStats(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load stats');
      } finally {
        setIsLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-blue animate-spin" />
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-red-700">
        {error || 'Failed to load dashboard stats'}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-ink">{t('admin.dashboard.title')}</h1>
        <p className="text-sepia/70 mt-1">{t('admin.dashboard.subtitle')}</p>
      </div>

      {/* Quick Stats Alert */}
      {stats.reports.pendingReports > 0 && (
        <Link
          to={routes.admin.reports}
          className="block bg-yellow-50 border border-yellow-200 rounded-xl p-4 hover:bg-yellow-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
            <span className="text-yellow-800 font-medium">
              {stats.reports.pendingReports} pending report
              {stats.reports.pendingReports > 1 ? 's' : ''} require attention
            </span>
          </div>
        </Link>
      )}

      {/* User Stats */}
      <div>
        <h2 className="text-lg font-medium text-ink mb-4">{t('admin.dashboard.users')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatsCard
            title={t('admin.stats.totalUsers')}
            value={stats.users.totalUsers}
            icon={Users}
            color="blue"
          />
          <StatsCard
            title={t('admin.stats.activeUsers')}
            value={stats.users.activeUsers}
            icon={UserCheck}
            color="green"
          />
          <StatsCard
            title={t('admin.stats.suspendedUsers')}
            value={stats.users.suspendedUsers}
            icon={UserMinus}
            color="yellow"
          />
          <StatsCard
            title={t('admin.stats.bannedUsers')}
            value={stats.users.bannedUsers}
            icon={UserX}
            color="red"
          />
          <StatsCard
            title={t('admin.stats.anonymizedUsers')}
            value={stats.users.anonymizedUsers}
            icon={ShieldAlert}
            color="gray"
          />
          <StatsCard
            title={t('admin.stats.adminUsers')}
            value={stats.users.adminUsers}
            icon={ShieldAlert}
            color="purple"
          />
        </div>
      </div>

      {/* Report Stats */}
      <div>
        <h2 className="text-lg font-medium text-ink mb-4">{t('admin.dashboard.reports')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatsCard
            title={t('admin.stats.totalReports')}
            value={stats.reports.totalReports}
            icon={Flag}
            color="blue"
          />
          <StatsCard
            title={t('admin.stats.pendingReports')}
            value={stats.reports.pendingReports}
            icon={Flag}
            color="yellow"
          />
          <StatsCard
            title={t('admin.stats.resolvedReports')}
            value={stats.reports.resolvedReports}
            icon={Flag}
            color="green"
          />
          <StatsCard
            title={t('admin.stats.dismissedReports')}
            value={stats.reports.dismissedReports}
            icon={Flag}
            color="gray"
          />
        </div>
      </div>

      {/* Letter Stats */}
      <div>
        <h2 className="text-lg font-medium text-ink mb-4">{t('admin.dashboard.letters')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatsCard
            title={t('admin.stats.totalLetters')}
            value={stats.letters.totalLetters}
            icon={FileText}
            color="blue"
          />
          <StatsCard
            title={t('admin.stats.inTransitLetters')}
            value={stats.letters.inTransitLetters}
            icon={FileText}
            color="purple"
          />
          <StatsCard
            title={t('admin.stats.deliveredLetters')}
            value={stats.letters.deliveredLetters}
            icon={FileText}
            color="green"
          />
          <StatsCard
            title={t('admin.stats.flaggedLetters')}
            value={stats.letters.flaggedLetters}
            icon={FileText}
            color="red"
          />
        </div>
      </div>

      {/* Session Stats */}
      <div>
        <h2 className="text-lg font-medium text-ink mb-4">{t('admin.dashboard.sessions')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatsCard
            title={t('admin.stats.totalSessions')}
            value={stats.sessions.totalSessions}
            icon={Monitor}
            color="blue"
          />
          <StatsCard
            title={t('admin.stats.activeSessions')}
            value={stats.sessions.activeSessions}
            icon={Monitor}
            color="green"
          />
          <StatsCard
            title={t('admin.stats.expiredSessions')}
            value={stats.sessions.expiredSessions}
            icon={Monitor}
            color="gray"
          />
        </div>
      </div>
    </div>
  );
}
